import { useForm } from '@tanstack/react-form'
import { useRouter } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { startTransition, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'
import { useNetworkConditions } from '@/shared/hooks/useNetworkConditions'
import { useAnalytics } from '@/shared/lib/analytics'
import { Button } from '@/shared/ui/button'
import { Container } from '@/shared/ui/container'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

import { DUMMY_PASSPORTS, SAMPLE_REQUEST_NUMBERS } from '../lib/dummy-data'
import {
  PassportSearchByName,
  type PassportSearchByName as PassportSearchByNameType,
  PassportSearchByNumber,
  type PassportSearchByNumber as PassportSearchByNumberType,
  type PassportSearchFilters,
} from '../schemas/passport'

type SearchMode = 'number' | 'name'

interface PassportSearchFormProps {
  onSearch: (filters: PassportSearchFilters, mode: SearchMode) => void
  onQueryChange?: (filters: PassportSearchFilters, mode: SearchMode) => void
  onScrollToResults?: () => void
}

export function PassportSearchForm({
  onSearch,
  onQueryChange,
  onScrollToResults,
}: PassportSearchFormProps) {
  const { t } = useTranslation('passports')
  const router = useRouter()
  const network = useNetworkConditions()
  const { capture } = useAnalytics()
  const [searchMode, setSearchMode] = useState<SearchMode>('name')
  // Local inputs for debounced interactive search
  const [numberInput, setNumberInput] = useState('')
  const [firstInput, setFirstInput] = useState('')
  const [middleInput, setMiddleInput] = useState('')
  const [lastInput, setLastInput] = useState('')
  const [isDebouncing, setIsDebouncing] = useState(false)

  const debouncedNumber = useDebouncedValue(numberInput, network.searchDebounceMs)
  const debouncedFirstInput = useDebouncedValue(firstInput, network.searchDebounceMs)
  const debouncedMiddleInput = useDebouncedValue(middleInput, network.searchDebounceMs)
  const debouncedLastInput = useDebouncedValue(lastInput, network.searchDebounceMs)

  // Track debouncing state for number search
  useEffect(() => {
    if (searchMode === 'number' && !network.preferManualSearch && numberInput.length >= 3) {
      setIsDebouncing(true)
      const timer = setTimeout(() => setIsDebouncing(false), network.searchDebounceMs)
      return () => clearTimeout(timer)
    }
    setIsDebouncing(false)
  }, [network.preferManualSearch, network.searchDebounceMs, numberInput, searchMode])

  // Track debouncing state for name search
  useEffect(() => {
    const nameQueryRaw = [firstInput, middleInput, lastInput].filter(Boolean).join(' ').trim()
    if (
      searchMode === 'name' &&
      !network.preferManualSearch &&
      nameQueryRaw.length >= network.searchMinCharacters
    ) {
      setIsDebouncing(true)
      const timer = setTimeout(() => setIsDebouncing(false), network.searchDebounceMs)
      return () => clearTimeout(timer)
    }
    setIsDebouncing(false)
  }, [
    firstInput,
    lastInput,
    middleInput,
    network.preferManualSearch,
    network.searchDebounceMs,
    network.searchMinCharacters,
    searchMode,
  ])

  const sanitizeRequestNumber = (value: string) =>
    value
      .replace(/[^0-9A-Za-z]/g, '')
      .trim()
      .toUpperCase()

  const sanitizeNameSegment = useCallback((value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return ''
    return trimmed
      .split(/\s+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ')
  }, [])

  const buildNameFilters = useCallback(
    (first: string, middle: string, last: string): PassportSearchFilters => {
      const filters: PassportSearchFilters = {}
      const sanitizedFirst = sanitizeNameSegment(first)
      const sanitizedMiddle = sanitizeNameSegment(middle)
      const sanitizedLast = sanitizeNameSegment(last)

      if (sanitizedFirst.length >= 3) filters.first_name = sanitizedFirst
      if (sanitizedMiddle.length >= 3) filters.middle_name = sanitizedMiddle
      if (sanitizedLast.length >= 3) filters.last_name = sanitizedLast

      return filters
    },
    [sanitizeNameSegment],
  )

  const numberForm = useForm({
    defaultValues: {
      requestNumber: '',
    } as PassportSearchByNumberType,
    onSubmit: async ({ value }) => {
      try {
        const validatedData = PassportSearchByNumber.parse(value)
        const sanitized = sanitizeRequestNumber(validatedData.requestNumber)

        // Track search started event
        const searchStartTime = Date.now()
        capture('passport_status_search_started', {
          method: 'by-passport-number',
          'request-number': sanitized,
          'network-status': navigator.onLine ? 'online' : 'offline',
          'search-start-time': searchStartTime,
        })

        // Check if a specific passport is found
        const foundPassport = DUMMY_PASSPORTS.find(
          (p) => p.requestNumber.toLowerCase() === sanitized.toLowerCase(),
        )

        if (foundPassport) {
          // Navigate to detail page
          router.navigate({
            to: '/passports/$passportId',
            params: { passportId: foundPassport.id },
          })
        } else {
          // Use existing search functionality for partial matches
          onSearch({ request_number: sanitized }, 'number')
          onScrollToResults?.()
        }
      } catch (error) {
        console.error('Validation error:', error)
      }
    },
  })

  const nameForm = useForm({
    defaultValues: {
      firstName: '',
      middleName: '',
      lastName: '',
    } as PassportSearchByNameType,
    onSubmit: async ({ value }) => {
      try {
        const validatedData = PassportSearchByName.parse(value)

        // Track search started event
        const searchStartTime = Date.now()
        const searchName = [
          validatedData.firstName,
          validatedData.middleName?.trim(),
          validatedData.lastName,
        ]
          .filter(Boolean)
          .join(' ')

        capture('passport_status_search_started', {
          method: 'by-name',
          'full-name': searchName,
          'has-middle-name': !!validatedData.middleName?.trim(),
          'network-status': navigator.onLine ? 'online' : 'offline',
          'search-start-time': searchStartTime,
        })

        // Check if a specific passport is found by name
        const searchNameLower = searchName.toLowerCase()
        const foundPassport = DUMMY_PASSPORTS.find((p) => p.name.toLowerCase() === searchNameLower)

        if (foundPassport) {
          // Navigate to detail page
          router.navigate({
            to: '/passports/$passportId',
            params: { passportId: foundPassport.id },
          })
        } else {
          // Use existing search functionality for partial matches
          const filters = buildNameFilters(
            validatedData.firstName ?? '',
            validatedData.middleName ?? '',
            validatedData.lastName ?? '',
          )
          onSearch(filters, 'name')
          onScrollToResults?.()
        }
      } catch (error) {
        console.error('Validation error:', error)
      }
    },
  })

  const handleToggleMode = () => {
    const next = searchMode === 'number' ? 'name' : 'number'
    startTransition(() => {
      setSearchMode(next)
      onQueryChange?.({}, next)
    })
  }

  // Debounced interactive search: trigger after >=3 characters
  useEffect(() => {
    if (searchMode !== 'number') return
    const q = debouncedNumber.trim()
    if (!onQueryChange) return
    if (network.preferManualSearch || !network.isOnline) return
    const sanitized = sanitizeRequestNumber(q)
    startTransition(() => {
      if (sanitized.length >= network.searchMinCharacters) {
        onQueryChange({ request_number: sanitized }, 'number')
      } else {
        onQueryChange({}, 'number')
      }
    })
  }, [
    debouncedNumber,
    network.isOnline,
    network.preferManualSearch,
    network.searchMinCharacters,
    onQueryChange,
    searchMode,
  ])

  useEffect(() => {
    if (searchMode !== 'name') return
    if (!onQueryChange) return
    if (network.preferManualSearch || !network.isOnline) return
    const filters = buildNameFilters(debouncedFirstInput, debouncedMiddleInput, debouncedLastInput)
    startTransition(() => {
      if (Object.keys(filters).length > 0) {
        onQueryChange(filters, 'name')
      } else {
        onQueryChange({}, 'name')
      }
    })
  }, [
    debouncedFirstInput,
    debouncedMiddleInput,
    debouncedLastInput,
    network.isOnline,
    network.preferManualSearch,
    searchMode,
    onQueryChange,
    buildNameFilters,
  ])

  return (
    <section className="py-10 sm:py-12 md:py-16">
      <Container>
        <div className="border-border/60 rounded-2xl border bg-transparent p-4 shadow-sm backdrop-blur-lg supports-[backdrop-filter]:bg-transparent sm:p-6">
          <div className="mx-auto max-w-2xl text-center">
            {/* Header */}
            <h1 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
              {t('search.title')}
            </h1>
            <h2 className="text-muted-foreground mt-4 text-lg">{t('search.subtitle')}</h2>

            {/* Search Form */}
            <div className="mt-8">
              {searchMode === 'number' ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    numberForm.handleSubmit()
                  }}
                  className="space-y-4"
                >
                  <numberForm.Field name="requestNumber">
                    {(field) => (
                      <div className="space-y-2">
                        <Label htmlFor="requestNumber" className="sr-only">
                          {t('search.form.requestNumber')}
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="requestNumber"
                            type="text"
                            placeholder={t('search.form.requestNumberPlaceholder')}
                            value={field.state.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.handleChange(v)
                              setNumberInput(v)
                            }}
                            onBlur={field.handleBlur}
                            className="flex-1"
                            aria-invalid={field.state.meta.errors.length > 0}
                          />
                          <Button type="submit" className="bg-foreground hover:bg-foreground/90">
                            {t('search.form.submit')}
                          </Button>
                        </div>
                        {isDebouncing ? (
                          <div className="text-muted-foreground flex items-center gap-2 text-xs">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            <span>{t('search.form.searching')}</span>
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-xs">
                            {t('search.form.minCharacters')}
                          </p>
                        )}
                      </div>
                    )}
                  </numberForm.Field>
                </form>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    nameForm.handleSubmit()
                  }}
                  className="space-y-4"
                >
                  <div className="grid gap-4 sm:grid-cols-3">
                    <nameForm.Field name="firstName">
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="firstName">{t('search.form.firstName')}</Label>
                          <Input
                            id="firstName"
                            type="text"
                            placeholder={t('search.form.firstNamePlaceholder')}
                            value={field.state.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.handleChange(v)
                              setFirstInput(v)
                            }}
                            onBlur={field.handleBlur}
                            aria-invalid={false}
                          />
                        </div>
                      )}
                    </nameForm.Field>

                    {/* Middle Name (optional) */}
                    <nameForm.Field name="middleName">
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="middleName">{t('search.form.middleName')}</Label>
                          <Input
                            id="middleName"
                            type="text"
                            placeholder={t('search.form.middleNamePlaceholder')}
                            value={field.state.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.handleChange(v)
                              setMiddleInput(v)
                            }}
                            onBlur={field.handleBlur}
                            aria-invalid={false}
                          />
                        </div>
                      )}
                    </nameForm.Field>

                    <nameForm.Field name="lastName">
                      {(field) => (
                        <div className="space-y-2">
                          <Label htmlFor="lastName">{t('search.form.lastName')}</Label>
                          <Input
                            id="lastName"
                            type="text"
                            placeholder={t('search.form.lastNamePlaceholder')}
                            value={field.state.value}
                            onChange={(e) => {
                              const v = e.target.value
                              field.handleChange(v)
                              setLastInput(v)
                            }}
                            onBlur={field.handleBlur}
                            aria-invalid={false}
                          />
                        </div>
                      )}
                    </nameForm.Field>
                  </div>

                  <Button type="submit" className="bg-foreground hover:bg-foreground/90 w-full">
                    {t('search.form.submit')}
                  </Button>
                  {isDebouncing ? (
                    <div className="text-muted-foreground flex items-center justify-center gap-2 text-xs">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span>{t('search.form.searching')}</span>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-xs">
                      {t('search.form.minCharactersName')}
                    </p>
                  )}
                </form>
              )}

              {/* Toggle Mode Button */}
              <button
                type="button"
                onClick={handleToggleMode}
                className="text-foreground mt-4 text-sm font-semibold underline underline-offset-4 hover:no-underline"
              >
                {' '}
                {searchMode === 'number'
                  ? t('search.toggle.useNameInstead')
                  : t('search.toggle.useNumberInstead')}
              </button>

              {/* Sample Request Numbers (only shown in number mode) */}
              {searchMode === 'number' && (
                <div className="text-muted-foreground mt-6 flex flex-wrap justify-center gap-2 text-sm">
                  {SAMPLE_REQUEST_NUMBERS.map((number) => (
                    <button
                      key={number}
                      type="button"
                      onClick={() => {
                        numberForm.setFieldValue('requestNumber', number)
                        // ensure debounced live search is triggered
                        setNumberInput(number)
                        startTransition(() => {
                          onQueryChange?.(
                            { request_number: sanitizeRequestNumber(number) },
                            'number',
                          )
                        })
                      }}
                      className="border-border bg-muted/50 hover:bg-muted rounded-md border px-3 py-1 text-xs"
                    >
                      {number}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
